import React, { useState, useEffect } from "react";
import "./ImageCarousel.scss";

interface ImageCarouselProps {
  images: {
    src: string;
    alt: string;
    thumbnail?: string;
  }[];
  autoPlay?: boolean;
  interval?: number;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  autoPlay = false,
  interval = 5000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);

  // Handle auto-play
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isAutoPlaying && images.length > 1) {
      timer = setInterval(() => {
        goToNext();
      }, interval);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, isAutoPlaying, interval, images.length]);

  const goToPrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
    setIsAutoPlaying(false);
  };

  const goToImage = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  if (images.length === 0) {
    return <div>No images to display</div>;
  }

  return (
    <div className="image-slider">
      <div className="slider__content">
        <button
          className="slider-control--button prev-button"
          onClick={goToPrev}
          aria-label="Previous image"
        >
          <svg className="icon" viewBox="0 0 24 24">
            <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
          </svg>
        </button>

        <div className="image-display">
          <img
            src={images[currentIndex].src}
            alt={images[currentIndex].alt}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </div>

        <button
          className="slider-control--button next-button"
          onClick={goToNext}
          aria-label="Next image"
        >
          <svg className="icon" viewBox="0 0 24 24">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
          </svg>
        </button>
      </div>

      <div className="slider-navigation">
        {images.map((image, index) => (
          // eslint-disable-next-line jsx-a11y/role-supports-aria-props
          <button
            key={index}
            className="nav-button"
            onClick={() => goToImage(index)}
            aria-label={`Go to image ${index + 1}`}
            aria-selected={index === currentIndex}
          >
            {image.thumbnail ? (
              <img
                src={image.thumbnail}
                alt={`Thumbnail for ${image.alt}`}
                className="thumbnail"
              />
            ) : (
              <span></span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ImageCarousel;
